import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { formatAgentLabel } from '@/lib/format';
import { AGENT_NAMES, type AgentName, type JobStatus } from '@/types/db';

/**
 * `<AgentProgressGrid>` — UI-SPEC § 3.2 mini 7-dot grid for jobs list cards.
 *
 * Each dot is a 8px circle color-coded by per-agent status. Order is locked
 * to UI-SPEC § 3.2 execution order (walkability · dog_friendliness ·
 * flood_zone · internet_providers · entertainment · property_images ·
 * compiled). The dots are NOT in the keyboard tab order on the JobRowCard
 * (the card-link wraps them and is itself the focusable element); each dot
 * exposes per-agent status via `aria-label` so SR users can drill in.
 *
 * The aggregate `aria-label` on the wrapping group is the primary SR
 * announcement: "5 of 7 agents complete, 1 running, 1 pending".
 *
 * Reduced-motion: running dots stop pulsing (motion-reduce:animate-none).
 */
export interface AgentProgressGridProps {
  agentProgress: Partial<Record<AgentName, JobStatus>>;
  className?: string;
}

const DOT_CLASS: Record<JobStatus, string> = {
  pending: 'bg-foam-50/15',
  running: 'bg-tide-400 animate-pulse motion-reduce:animate-none',
  completed: 'bg-moss-500',
  failed: 'bg-coral-500',
};

export function AgentProgressGrid({
  agentProgress,
  className,
}: AgentProgressGridProps) {
  const completed = AGENT_NAMES.filter(
    (n) => agentProgress[n] === 'completed',
  ).length;
  const running = AGENT_NAMES.filter(
    (n) => agentProgress[n] === 'running',
  ).length;
  const failed = AGENT_NAMES.filter(
    (n) => agentProgress[n] === 'failed',
  ).length;
  const pending = AGENT_NAMES.length - completed - running - failed;
  const ariaLabel =
    `Agent progress: ${completed} of ${AGENT_NAMES.length} agents complete` +
    (running > 0 ? `, ${running} running` : '') +
    (failed > 0 ? `, ${failed} failed` : '') +
    (pending > 0 ? `, ${pending} pending` : '');

  return (
    <TooltipProvider delayDuration={200}>
      <div
        role="group"
        aria-label={ariaLabel}
        className={cn('flex items-center gap-1', className)}
      >
        {AGENT_NAMES.map((name) => {
          const status: JobStatus = agentProgress[name] ?? 'pending';
          const label = formatAgentLabel(name);
          return (
            <Tooltip key={name}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  aria-label={`${label}: ${status}`}
                  className={cn(
                    'relative w-2 h-2 rounded-full',
                    // Larger invisible hit area for touch (UI-SPEC § 9.4):
                    'before:absolute before:inset-[-12px] before:content-[""]',
                    DOT_CLASS[status],
                  )}
                />
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                {label} · {status}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
